import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/comics';

export default function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '布丁漫画',
    url: 'https://puddingcomic.com',
    description: aboutInfo.mission,
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`关于布丁漫画 - ${aboutInfo.mission}。了解布丁漫画的品牌故事、平台信誉和联系方式，海量正版韩国漫画免费阅读。`}
        keywords="布丁漫画,关于我们,韩国漫画平台,正版韩漫,联系方式,漫画阅读"
        canonicalUrl="https://puddingcomic.com/about"
        schema={aboutSchema}
      />
      <Navbar />

      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">关于我们</span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mt-4 mb-3">
                了解布丁漫画
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
              {aboutInfo.stats.map((stat, i) => (
                <div key={i} className="text-center p-5 rounded-lg bg-background-100 border border-background-200">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-building-4-line text-primary-500" />
                  品牌介绍
                </h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  {aboutInfo.description.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-primary-500" />
                  平台信誉
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      icon: 'ri-checkbox-circle-line',
                      title: '合法授权经营',
                      desc: '所有漫画作品均获得韩国版权方合法授权，严格遵守知识产权法律法规。',
                    },
                    {
                      icon: 'ri-user-star-line',
                      title: '创作者共赢',
                      desc: '我们确保每一位创作者都能获得合理的版权收益，促进漫画行业健康发展。',
                    },
                    {
                      icon: 'ri-shield-star-line',
                      title: '数据安全保障',
                      desc: '采用先进的加密技术保护用户隐私数据，通过多项安全认证，阅读环境安全可靠。',
                    },
                    {
                      icon: 'ri-service-line',
                      title: '优质客户服务',
                      desc: '专业的客服团队随时为您解决问题，确保每一位读者都能获得满意的阅读体验。',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-500" />
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: aboutInfo.contact.email },
                    { icon: 'ri-phone-line', label: '客服电话', value: aboutInfo.contact.phone },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: aboutInfo.contact.address },
                    { icon: 'ri-wechat-line', label: '官方微信', value: aboutInfo.contact.wechat },
                    { icon: 'ri-weibo-line', label: '官方微博', value: aboutInfo.contact.weibo },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                        <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-lg overflow-hidden bg-background-200 h-64 md:h-full min-h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3058.9!2d116.4803!3d39.9962!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f05437c6f3c5e7%3A0xd6f0b1b1d1a1b1a1!2z5pyb5Lqs5biCRU9I!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                    width="100%"
                    height="100%"
                    className="border-0 w-full h-full"
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="布丁漫画 - 公司地址"
                    aria-label="布丁漫画公司地图位置"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}